import { useEffect, useState } from 'react';
import { Button, ErrorNotice, Modal } from '@kiipu/ui';
import { ArrowDown, CheckCircle, WarningCircle } from '@phosphor-icons/react';
import type { DesktopCaptureAction, DesktopCaptureJob } from '@kiipu/desktop-protocol';
import { desktopBridge, platformState, usePlatform } from '../lib/client-platform';
import { useDesktopCaptures } from '../lib/desktop-captures';

const phaseLabels: Record<DesktopCaptureJob['phase'], string> = {
  queued: '等待本地读取',
  loading: '正在读取网页',
  needs_interaction: '需要操作网页',
  local: '正文已在本机保存',
  uploading: '正在上传图片',
  submitting: '正在提交保存',
  archiving: '正在确认归档',
  completed: '保存完成',
  partial: '已保存部分内容',
  failed: '保存未完成',
  cancelled: '已取消',
};
const finished = new Set<DesktopCaptureJob['phase']>([
  'completed',
  'partial',
  'failed',
  'cancelled',
]);
function sourceHost(value: string) {
  try {
    return new URL(value).hostname;
  } catch {
    return '来源网页';
  }
}

function CaptureRow({ job }: { job: DesktopCaptureJob }) {
  const [pending, setPending] = useState<DesktopCaptureAction | null>(null);
  const [error, setError] = useState('');
  const owner = platformState()?.user?.id;
  const run = async (action: DesktopCaptureAction) => {
    const bridge = desktopBridge();
    if (!bridge || pending) return;
    setPending(action);
    setError('');
    try {
      await bridge.controlCapture(job.id, action);
    } catch (cause) {
      if (platformState()?.user?.id === owner)
        setError(cause instanceof Error ? cause.message : '操作未完成，请重试。');
    } finally {
      if (platformState()?.user?.id === owner) setPending(null);
    }
  };
  const needsReading = ['needs_interaction', 'failed', 'partial'].includes(job.phase);
  return (
    <li className="desktop-capture-row" aria-busy={pending !== null || undefined}>
      <div className="desktop-capture-heading">
        <strong>{job.title || sourceHost(job.url)}</strong>
        <span className="desktop-capture-phase" data-phase={job.phase}>
          {phaseLabels[job.phase]}
        </span>
      </div>
      <p className="desktop-capture-source">{sourceHost(job.url)}</p>
      {job.images ? (
        <p className="desktop-capture-description">
          图片 {job.images.ready} / {job.images.total}
        </p>
      ) : null}
      {job.error ? <p className="desktop-capture-description">{job.error}</p> : null}
      <div className="desktop-capture-actions">
        {needsReading ? (
          <Button variant="secondary" disabled={pending !== null} onClick={() => void run('open')}>
            打开网页
          </Button>
        ) : null}
        {needsReading ? (
          <Button
            variant="secondary"
            disabled={pending !== null}
            onClick={() => void run('reread')}
          >
            重新读取
          </Button>
        ) : null}
        {job.canSavePartial || job.phase === 'needs_interaction' ? (
          <Button disabled={pending !== null} onClick={() => void run('save-partial')}>
            保存现有内容
          </Button>
        ) : null}
        {job.phase === 'failed' ? (
          <Button disabled={pending !== null} onClick={() => void run('retry')}>
            重试保存
          </Button>
        ) : null}
        {job.itemId ? (
          <Button
            variant="secondary"
            disabled={pending !== null}
            onClick={() => {
              void desktopBridge()
                ?.openReader(job.itemId!)
                .catch((cause: Error) => setError(cause.message));
            }}
          >
            查看内容
          </Button>
        ) : null}
        {!['completed', 'cancelled'].includes(job.phase) ? (
          <Button
            variant="secondary"
            disabled={pending !== null}
            onClick={() => void run('cancel')}
          >
            取消
          </Button>
        ) : null}
      </div>
      <ErrorNotice error={error} />
    </li>
  );
}

/** Available in both the main workspace and independent reader windows. */
export function DesktopCaptures() {
  const state = usePlatform();
  const jobs = useDesktopCaptures();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener('kiipu:show-captures', show);
    return () => window.removeEventListener('kiipu:show-captures', show);
  }, []);
  if (!state?.user || !jobs.length || state.windowKind === 'settings') return null;
  const active = jobs.filter((job) => !finished.has(job.phase)).length;
  const attention = jobs.filter((job) =>
    ['needs_interaction', 'failed', 'partial'].includes(job.phase),
  ).length;
  const Icon = attention ? WarningCircle : active ? ArrowDown : CheckCircle;
  return (
    <>
      <button
        type="button"
        className="desktop-capture-status"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <Icon size={17} />
        <span>
          本地保存{attention ? ` · ${attention} 项需要处理` : active ? ` · ${active} 项进行中` : ''}
        </span>
      </button>
      <Modal
        open={open}
        onOpenChange={setOpen}
        title="本地保存"
        description="正文和图片由这台设备读取，随后同步到 Kiipu。需要登录、验证或展开时，可打开网页继续。"
        className="desktop-capture-modal"
      >
        <ol className="desktop-capture-list">
          {jobs.map((job) => (
            <CaptureRow key={job.id} job={job} />
          ))}
        </ol>
        <div className="modal-actions">
          <Button onClick={() => setOpen(false)}>完成</Button>
        </div>
      </Modal>
    </>
  );
}
